import { Button } from '@heroui/react';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import AvatarEditor from 'react-avatar-editor';

import { ImageEditor } from './ImageEditor';
import { UserImage } from './UserImage';

export interface ProfilePictureProps {
  /** Function called when user saves the edited image */
  onImageSave: (canvas: HTMLCanvasElement | null) => void;
  /** Current user data with profile image */
  user?: {
    id: string;
    name?: string;
    profileImage?: {
      url?: string;
      mediumSquare?: string;
    };
  } | null;
  /** Whether the component is visible/active */
  isOpen?: boolean;
  /** Whether the component is disabled */
  isDisabled?: boolean;
  /** Custom label for the section */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Error message */
  errorMessage?: string;
  'data-testid'?: string;
}

interface ImageState {
  rotate: number;
  scale: number;
  image: File | string | null;
  editMode: boolean;
  loading: boolean;
  userImageUrl: string;
}

export const ProfilePicture: React.FC<ProfilePictureProps> = ({
  onImageSave,
  user,
  isOpen = true,
  isDisabled = false,
  label = 'Profile Picture',
  helperText = 'Upload and edit a profile picture for the user',
  errorMessage,
  'data-testid': dataTestId,
}) => {
  const editorRef = useRef<AvatarEditor>(null);

  const [state, setState] = useState<ImageState>({
    rotate: 0,
    scale: 1,
    image: null,
    editMode: false,
    loading: false,
    userImageUrl: '',
  });

  // Load existing user image
  useEffect(() => {
    if (user?.profileImage?.url) {
      setState((prev) => ({
        ...prev,
        userImageUrl: user.profileImage?.url || '',
        loading: false,
      }));
    } else {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [user]);

  const handleImageDrop = useCallback((files: File[]) => {
    if (files.length > 0) {
      setState((prev) => ({
        ...prev,
        image: files[0],
        editMode: true,
        rotate: 0,
        scale: 1,
      }));
    }
  }, []);

  const handleRotateLeft = useCallback(() => {
    setState((prev) => ({
      ...prev,
      rotate: prev.rotate - 90,
    }));
  }, []);

  const handleRotateRight = useCallback(() => {
    setState((prev) => ({
      ...prev,
      rotate: prev.rotate + 90,
    }));
  }, []);

  const handleScaleChange = useCallback((newScale: number) => {
    setState((prev) => ({
      ...prev,
      scale: newScale,
    }));
  }, []);

  const handleSaveImage = useCallback(() => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      onImageSave(canvas);
      setState((prev) => ({
        ...prev,
        editMode: false,
      }));
    }
  }, [onImageSave]);

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      {/* Header - matches the reference UI */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">{label}</h3>
        <Button
          color="default"
          variant="solid"
          isDisabled={!state.editMode || state.loading || isDisabled}
          onPress={handleSaveImage}
          className="bg-default-400 text-white px-6 py-2 rounded-full font-medium"
        >
          SAVE PICTURE
        </Button>
      </div>

      {/* Content - Two column layout like the reference */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[300px]">
        {/* Left: Image Preview */}
        <div className="flex items-center justify-center">
          {state.loading ? (
            <div className="text-center">
              <p className="text-default-500">Loading image...</p>
            </div>
          ) : (
            <UserImage
              editorRef={editorRef}
              userImage={state.image || state.userImageUrl || ''}
              scale={state.scale}
              rotation={state.rotate}
              allowEdit={state.editMode}
              size={200}
            />
          )}
        </div>

        {/* Right: Upload Area */}
        <div className="flex items-center justify-center">
          <ImageEditor
            isEditMode={state.editMode}
            onDrop={handleImageDrop}
            onScaleChange={handleScaleChange}
            onRotateLeft={handleRotateLeft}
            onRotateRight={handleRotateRight}
            scale={state.scale}
            isDisabled={isDisabled}
          />
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <p className="text-sm text-danger mt-2">{errorMessage}</p>
      )}

      {/* Helper Text */}
      {helperText && !errorMessage && (
        <p className="text-xs text-default-500">{helperText}</p>
      )}
    </div>
  );
};
